import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

const authHeaders = (): Record<string, string> => {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return {};
  const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
  const token = parsed.state?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ─── Types ────────────────────────────────────────────────────────────────────

export type ImportSourceType = 'open_dataset' | 'official' | 'user_provided';
export type ImportSessionStatus = 'draft' | 'upload_ready' | 'mapping_ready' | 'committed';

export interface ColumnSuggestion {
  internalField: string;
  suggestedColumn: string;
  confidence: 'high' | 'medium';
}

export interface FieldMapping {
  latitude: string;
  longitude: string;
  useType?: string;
  residentialType?: string;
  floors?: string;
  heightM?: string;
  footprintAreaM2?: string;
  builtAreaM2?: string;
  unitsCount?: string;
  dwellingsCount?: string;
  notes?: string;
  source?: string;
}

export interface BuildingImportSession {
  id: string;
  projectId: string;
  sourceType: ImportSourceType;
  sourceDataset: string | null;
  sourceName: string | null;
  status: ImportSessionStatus;
  uploadedFileName: string | null;
  detectedColumns: string[] | null;
  previewRows: Record<string, string>[] | null;
  fieldMapping: FieldMapping | null;
  suggestions: ColumnSuggestion[] | null;
  autoMapping: Record<string, string> | null;
  requiresManualMapping: boolean | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ValidationIssue {
  rowIndex: number;
  severity: 'error' | 'warning';
  code: string;
  message: string;
}

export interface ValidationSummary {
  totalRows: number;
  errorRows: number;
  warningRows: number;
  duplicateCandidates: number;
  missingCoordinates: number;
  invalidCoordinates: number;
}

export interface TransformedPreviewResponse {
  sessionId: string;
  fieldMapping: FieldMapping;
  rows: Array<Record<string, unknown>>;
  totalPreviewRows: number;
  validationSummary: ValidationSummary;
  validationIssues: ValidationIssue[];
}

export interface CommitResult {
  totalRows: number;
  importedRows: number;
  skippedRows: number;
  errorRows: number;
  warningRows: number;
  duplicateCandidates: number;
  issuesByCode: Record<string, number>;
}

export interface BoundingBox {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
}

export interface GoogleBuildingsClipResult {
  sessionId: string;
  originalFileName: string;
  originalSizeBytes: number;
  clippedFileName: string;
  clippedSizeBytes: number;
  totalRowsScanned: number;
  rowsKept: number;
  rowsSkipped: number;
  /** BBox del proyecto (AOI usado para el recorte). */
  projectBbox: BoundingBox;
  /** BBox real del archivo (extensión de todos los registros escaneados). */
  fileBbox: BoundingBox;
  /** true si fileBbox intersecta projectBbox. */
  intersects: boolean;
  reductionRatio: number;
}

// ─── API ──────────────────────────────────────────────────────────────────────

export const buildingImportSessionApi = {
  async createSession(
    projectId: string,
    dto: { sourceType: ImportSourceType; sourceDataset?: string; sourceName?: string },
  ): Promise<BuildingImportSession> {
    const res = await axios.post<BuildingImportSession>(
      `${API_URL}/projects/${projectId}/buildings/sessions`,
      dto,
      { headers: authHeaders() },
    );
    return res.data;
  },

  async uploadFile(
    projectId: string,
    sessionId: string,
    file: File,
  ): Promise<BuildingImportSession> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post<BuildingImportSession>(
      `${API_URL}/projects/${projectId}/buildings/sessions/${sessionId}/upload`,
      formData,
      { headers: authHeaders() },
    );
    return res.data;
  },

  async putMapping(
    projectId: string,
    sessionId: string,
    mapping: FieldMapping,
  ): Promise<BuildingImportSession> {
    const res = await axios.put<BuildingImportSession>(
      `${API_URL}/projects/${projectId}/buildings/sessions/${sessionId}/mapping`,
      mapping,
      { headers: authHeaders() },
    );
    return res.data;
  },

  async getPreview(
    projectId: string,
    sessionId: string,
  ): Promise<TransformedPreviewResponse> {
    const res = await axios.get<TransformedPreviewResponse>(
      `${API_URL}/projects/${projectId}/buildings/sessions/${sessionId}/preview`,
      { headers: authHeaders() },
    );
    return res.data;
  },

  async commitSession(
    projectId: string,
    sessionId: string,
    opts: { clearPrevious?: boolean } = {},
  ): Promise<CommitResult> {
    const res = await axios.post<CommitResult>(
      `${API_URL}/projects/${projectId}/buildings/sessions/${sessionId}/commit`,
      opts,
      { headers: authHeaders() },
    );
    return res.data;
  },

  async getSession(
    projectId: string,
    sessionId: string,
  ): Promise<BuildingImportSession> {
    const res = await axios.get<BuildingImportSession>(
      `${API_URL}/projects/${projectId}/buildings/sessions/${sessionId}`,
      { headers: authHeaders() },
    );
    return res.data;
  },

  /**
   * Sube un archivo Google Open Buildings grande al endpoint especial de recorte.
   * El servidor lo escribe a disco via streaming, recorta por AOI y devuelve stats.
   * Usa XMLHttpRequest para reportar progreso de carga.
   */
  uploadLargeGoogleBuildings(
    projectId: string,
    sessionId: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<GoogleBuildingsClipResult> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = `${API_URL}/projects/${projectId}/buildings/sessions/${sessionId}/upload-large-google-buildings`;

      xhr.open('POST', url);

      const headers = authHeaders();
      Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));

      if (onProgress) {
        xhr.upload.onprogress = (e): void => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        };
      }

      xhr.onload = (): void => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as GoogleBuildingsClipResult);
          } catch {
            reject(new Error('Respuesta inválida del servidor'));
          }
        } else {
          try {
            const body = JSON.parse(xhr.responseText) as { message?: string };
            const msg = Array.isArray(body.message) ? body.message.join(', ') : (body.message ?? `HTTP ${xhr.status}`);
            reject(new Error(msg));
          } catch {
            reject(new Error(`HTTP ${xhr.status}`));
          }
        }
      };

      xhr.onerror = (): void => reject(new Error('Error de red al subir el archivo'));
      xhr.ontimeout = (): void => reject(new Error('Timeout al subir el archivo'));

      // No timeout — large files can take several minutes
      xhr.timeout = 0;

      const formData = new FormData();
      formData.append('file', file);
      xhr.send(formData);
    });
  },
};
